import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useChartColorDefinitions } from '@/components/charts/constants';
import { Legend, LegendSection } from '@/components/charts/Legend/Legend';
import { StackedHorizontalBarTooltip } from '@/components/charts/StackedHorizontalBar/StackedHorizontalBar';
import {
  StackedHorizontalBarGroup,
  StackedHorizontalBarsWithLabels,
} from '@/components/charts/StackedHorizontalBarGroup/StackedHorizontalBarGroup';
import { CompoundHeader } from '@/components/display/CompoundHeader/CompoundHeader';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { AfterDeath, EntityInEstateStatus } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { UnreachableError } from '@/utils/errors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { HypotheticalSaleLoanFormShape } from '../EstateWaterfallHypotheticalSaleLoanModal.types';
import { useSaleLoanFormContext } from '../Form/context/SaleLoanForm.context';
import {
  SaleLoanIllustration_EstateWaterfallFragment,
  SaleLoanIllustration_EstateWaterfallVizNodeFragment,
  SaleLoanIllustration_GiftTaxSummaryFragment,
  SaleLoanIllustration_TaxSummaryV2Fragment,
} from './graphql/SaleLoanIllustration.generated';
import { PROJECTION_PSEUDO_ID } from './SaleLoanIllustrationContent.utils';
import { SaleLoanIllustrationScenarioSelector } from './SaleLoanIllustrationScenarioSelector';

interface BarChartSectionValues {
  inEstateValue: Decimal;
  outOfEstateValue: Decimal;
  taxValue: Decimal;
  giftedAssetsValue: Decimal;
}

const emptyBarChartSectionValues: BarChartSectionValues = {
  inEstateValue: new Decimal(0),
  outOfEstateValue: new Decimal(0),
  taxValue: new Decimal(0),
  giftedAssetsValue: new Decimal(0),
};

function getTaxValueFromSummary(
  taxSummary:
    | SaleLoanIllustration_TaxSummaryV2Fragment
    | SaleLoanIllustration_GiftTaxSummaryFragment
    | null
) {
  if (!taxSummary) return new Decimal(0);
  let federalTax = new Decimal(0);
  if ('federalGiftTax' in taxSummary) {
    federalTax = taxSummary.federalGiftTax?.tax ?? new Decimal(0);
  } else if ('federalTax' in taxSummary) {
    federalTax = taxSummary.federalTax?.tax ?? new Decimal(0);
  }

  let stateTax = new Decimal(0);
  if ('stateTax' in taxSummary) {
    stateTax = sumDecimalJS(taxSummary.stateTax?.map((t) => t.tax) ?? []);
  }

  let gstTax = new Decimal(0);
  if ('gstGiftTax' in taxSummary) {
    gstTax = taxSummary.gstGiftTax?.tax ?? new Decimal(0);
  } else if ('gstTax' in taxSummary) {
    gstTax = taxSummary.gstTax?.tax ?? new Decimal(0);
  }

  return federalTax.add(stateTax).add(gstTax);
}

function getValuesForSection(
  nodes: SaleLoanIllustration_EstateWaterfallVizNodeFragment[],
  deathEventTaxSummary:
    | SaleLoanIllustration_TaxSummaryV2Fragment
    | SaleLoanIllustration_GiftTaxSummaryFragment
    | null,
  targetDeathEvent: AfterDeath
): Omit<BarChartSectionValues, 'giftedAssetsValue'> {
  const values = {
    inEstateValue: new Decimal(0),
    outOfEstateValue: new Decimal(0),
    taxValue: getTaxValueFromSummary(deathEventTaxSummary),
  };

  nodes.forEach((n) => {
    // Don't sum the value of this node unless it's the target death event
    if (n.afterDeath !== targetDeathEvent) return;

    switch (n.inEstateStatus) {
      case EntityInEstateStatus.InEstate:
        values.inEstateValue = values.inEstateValue.add(n.value);
        break;
      case EntityInEstateStatus.OutOfEstate:
        values.outOfEstateValue = values.outOfEstateValue.add(n.value);
        break;
    }
  });

  return values;
}

function getBarChartValues(
  waterfall: SaleLoanIllustration_EstateWaterfallFragment | null,
  afterDeath: AfterDeath
): { withPlan: BarChartSectionValues; noPlan: BarChartSectionValues } {
  if (!waterfall) {
    return {
      withPlan: emptyBarChartSectionValues,
      noPlan: emptyBarChartSectionValues,
    };
  }

  const projectedLoan =
    waterfall.visualizationWithSaleLoanProjections.loans.find(
      (l) => l.pseudoID === PROJECTION_PSEUDO_ID
    );
  const valueHistoryItem = projectedLoan?.valueHistory[0];
  const giftedAssetsValue =
    valueHistoryItem?.beginningOfYearValue ?? new Decimal(0);

  const withPlanTaxSummary = (() => {
    switch (afterDeath) {
      case AfterDeath.None:
        return waterfall.visualizationWithSaleLoanProjections
          .beforeFirstDeathTaxSummaryV2;
      case AfterDeath.First:
        return waterfall.visualizationWithSaleLoanProjections
          .firstDeathTaxSummaryV2;
      case AfterDeath.Second:
        return waterfall.visualizationWithSaleLoanProjections
          .secondDeathTaxSummaryV2;
      default:
        throw new UnreachableError({
          case: afterDeath,
          message: `Invalid after death scenario: ${afterDeath}`,
        });
    }
  })();

  const withoutPlanTaxSummary = (() => {
    switch (afterDeath) {
      case AfterDeath.None:
        return waterfall.visualizationWithoutSaleLoanProjections
          .beforeFirstDeathTaxSummaryV2;
      case AfterDeath.First:
        return waterfall.visualizationWithoutSaleLoanProjections
          .firstDeathTaxSummaryV2;
      case AfterDeath.Second:
        return waterfall.visualizationWithoutSaleLoanProjections
          .secondDeathTaxSummaryV2;
    }
  })();

  const values = {
    withPlan: {
      ...getValuesForSection(
        waterfall.visualizationWithSaleLoanProjections.nodes,
        withPlanTaxSummary ?? null,
        afterDeath
      ),
      giftedAssetsValue,
    },
    noPlan: {
      ...getValuesForSection(
        waterfall.visualizationWithoutSaleLoanProjections.nodes,
        withoutPlanTaxSummary ?? null,
        afterDeath
      ),
      giftedAssetsValue: new Decimal(0),
    },
  };

  return values;
}

export function SaleLoanIllustrationBarChart() {
  const { simulatedWaterfall: waterfall } = useSaleLoanFormContext();
  const chartColorDefinitions = useChartColorDefinitions();
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const semanticColorMap = useMemo(() => {
    return {
      IN_ESTATE: chartColorDefinitions.PRIMARY.backgroundColor,
      OUT_OF_ESTATE: chartColorDefinitions.SECONDARY.backgroundColor,
      GIFT_AND_ESTATE_TAXES: chartColorDefinitions.NEGATIVE.backgroundColor,
    };
  }, [chartColorDefinitions]);

  const legendSections: LegendSection[] = [
    { label: 'In-estate', color: semanticColorMap.IN_ESTATE },
    { label: 'Out-of-estate', color: semanticColorMap.OUT_OF_ESTATE },
    {
      label: 'Gift & estate taxes',
      color: semanticColorMap.GIFT_AND_ESTATE_TAXES,
    },
  ];

  const [afterDeathScenario, kind] = useWatch({
    control,
    name: ['illustrationScenario', '_kind'],
  });

  const { withPlan, noPlan } = useMemo(
    () => getBarChartValues(waterfall, afterDeathScenario),
    [afterDeathScenario, waterfall]
  );

  const sectionTextColor = COLORS.TEXT.PRIMARY;
  const bars: StackedHorizontalBarsWithLabels[] = useMemo(
    () => [
      {
        label: 'No plan',
        sections: [
          {
            groupName: 'No plan',
            value: noPlan.inEstateValue.toNumber(),
            color: semanticColorMap.IN_ESTATE,
            sectionTextColor,
            label: 'In-estate',
            tooltip: (
              <StackedHorizontalBarTooltip
                value={new Decimal(noPlan.inEstateValue)}
                label="In-estate"
              />
            ),
          },
          {
            groupName: 'No plan',
            value: noPlan.outOfEstateValue.toNumber(),
            color: semanticColorMap.OUT_OF_ESTATE,
            sectionTextColor,
            label: 'Out of estate',
            tooltip: (
              <StackedHorizontalBarTooltip
                value={new Decimal(noPlan.outOfEstateValue)}
                label="Out of estate"
              />
            ),
          },
          {
            groupName: 'No plan',
            value: noPlan.taxValue.toNumber(),
            color: semanticColorMap.GIFT_AND_ESTATE_TAXES,
            sectionTextColor,
            label: 'Gift & estate taxes',
            tooltip: (
              <StackedHorizontalBarTooltip
                value={new Decimal(noPlan.taxValue)}
                label="Gift & estate taxes"
              />
            ),
          },
        ],
      },
      {
        label: kind === 'sale' ? 'With sale' : 'With loan',
        sections: [
          {
            groupName: 'With sale',
            value: withPlan.inEstateValue.toNumber(),
            color: semanticColorMap.IN_ESTATE,
            sectionTextColor,
            label: 'In-estate',
            tooltip: (
              <StackedHorizontalBarTooltip
                value={new Decimal(withPlan.inEstateValue)}
                label="In-estate"
              />
            ),
          },
          {
            groupName: 'With sale',
            value: withPlan.outOfEstateValue.toNumber(),
            color: semanticColorMap.OUT_OF_ESTATE,
            sectionTextColor,
            label: 'Out of estate',
            tooltip: (
              <StackedHorizontalBarTooltip
                value={new Decimal(withPlan.outOfEstateValue)}
                label="Out of estate"
              />
            ),
          },
          {
            groupName: 'With sale',
            value: withPlan.taxValue.toNumber(),
            color: semanticColorMap.GIFT_AND_ESTATE_TAXES,
            sectionTextColor,
            label: 'Gift & estate taxes',
            tooltip: (
              <StackedHorizontalBarTooltip
                value={new Decimal(withPlan.taxValue)}
                label="Gift & estate taxes"
              />
            ),
          },
        ],
      },
    ],
    [
      noPlan.inEstateValue,
      noPlan.outOfEstateValue,
      noPlan.taxValue,
      semanticColorMap.IN_ESTATE,
      semanticColorMap.OUT_OF_ESTATE,
      semanticColorMap.GIFT_AND_ESTATE_TAXES,
      sectionTextColor,
      kind,
      withPlan.inEstateValue,
      withPlan.outOfEstateValue,
      withPlan.taxValue,
    ]
  );

  // wealth transferred is out of estate assets (with sale) - out of estate assets (no sale)
  const wealthTransferred = withPlan.outOfEstateValue.sub(
    noPlan.outOfEstateValue
  );

  // tax savings is gift and estate taxes (with sale) - gift and estate taxes (no sale)
  const taxSavings = noPlan.taxValue.sub(withPlan.taxValue);

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <SaleLoanIllustrationScenarioSelector />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" width="100%">
          <Box flexGrow={1}>
            <StackedHorizontalBarGroup
              bars={bars}
              showLegend={false}
              headerSpacing={0}
            />
          </Box>
          <Stack spacing={1} flexShrink={0}>
            {/* Show "Wealth transferred" for no death scenario or first death in two-grantor households.
                For two-grantor households, after the first death, we want to show wealth transferred
                rather than tax savings to better represent the estate planning benefit. */}
            {afterDeathScenario === AfterDeath.None ||
            (afterDeathScenario === AfterDeath.First &&
              isTwoClientHousehold) ? (
              <CompoundHeader
                subheading="Wealth transferred"
                heading={formatCurrencyNoDecimals(wealthTransferred)}
                invertLabel
              />
            ) : (
              <CompoundHeader
                subheading="Tax savings"
                heading={formatCurrencyNoDecimals(taxSavings)}
                invertLabel
              />
            )}
          </Stack>
        </Stack>
        <Box flexShrink={0}>
          <Legend sections={legendSections} />
        </Box>
      </Stack>
    </Card>
  );
}
